import { useMemo, useState } from "react";
import {
  BLOG_CATEGORIES,
  BLOG_POSTS,
  postMatchesQuery,
} from "../../data/blog";
import BlogCard from "./BlogCard";

const INITIAL_VISIBLE = 4;

export default function BlogHome() {
  const [activeCat, setActiveCat] = useState("All");
  const [query, setQuery] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);

  const filteredPosts = useMemo(() => {
    return BLOG_POSTS.filter((post) => {
      const catMatch = activeCat === "All" || post.cat === activeCat;
      return catMatch && postMatchesQuery(post, query);
    });
  }, [activeCat, query]);

  const visiblePosts = filteredPosts.slice(0, visibleCount);
  const trimmedQuery = query.trim();
  const showLoadMore =
    !trimmedQuery &&
    filteredPosts.length > 0 &&
    visibleCount < filteredPosts.length;
  const showEmpty = filteredPosts.length === 0;

  const handleCategory = (cat) => {
    setActiveCat(cat);
    setVisibleCount(INITIAL_VISIBLE);
  };

  const handleQueryChange = (value) => {
    setQuery(value);
    setVisibleCount(INITIAL_VISIBLE);
  };

  return (
    <section className="blog-home" aria-label="Blog">
      <div className="blog-home__masthead">
        <div className="blog-home__masthead-grid">
          <div>
            <div className="blog-home__eyebrow">
              FIELD NOTES · {BLOG_POSTS.length} {BLOG_POSTS.length === 1 ? "ENTRY" : "ENTRIES"}
            </div>
            <h1 className="blog-home__title">The Blog</h1>
          </div>
          <p className="blog-home__blurb">
            Thoughts on product, technology, photography, and the road — a notebook
            for ideas in progress.
          </p>
        </div>

        <div className="blog-home__chips" role="tablist" aria-label="Categories">
          {BLOG_CATEGORIES.map((cat) => (
            <button
              key={cat}
              type="button"
              role="tab"
              aria-selected={activeCat === cat}
              className={`blog-home__chip${activeCat === cat ? " blog-home__chip--active" : ""}`}
              onClick={() => handleCategory(cat)}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      <div className="blog-home__search-wrap">
        <div
          className={`blog-home__search${searchFocused ? " blog-home__search--focused" : ""}`}
        >
          <svg
            className="blog-home__search-icon"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="7" />
            <line x1="21" y1="21" x2="16.5" y2="16.5" />
          </svg>
          <input
            type="search"
            className="blog-home__search-input"
            placeholder='Search posts — try "ai", product, technology…'
            value={query}
            autoComplete="off"
            aria-label="Search blog posts"
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            onChange={(event) => handleQueryChange(event.target.value)}
          />
          {query ? (
            <button
              type="button"
              className="blog-home__search-clear"
              aria-label="Clear search"
              onClick={() => handleQueryChange("")}
            >
              ×
            </button>
          ) : null}
        </div>
      </div>

      {!showEmpty ? (
        <div className="blog-home__grid">
          {visiblePosts.map((post) => (
            <BlogCard key={post.id} post={post} query={query} />
          ))}
        </div>
      ) : null}

      {showEmpty ? (
        <div className="blog-home__empty">
          <div className="blog-home__empty-icon" aria-hidden="true">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
            >
              <circle cx="11" cy="11" r="7" />
              <line x1="21" y1="21" x2="16.5" y2="16.5" />
              <line x1="8.5" y1="11" x2="13.5" y2="11" />
            </svg>
          </div>
          <h2 className="blog-home__empty-title">Nothing down that trail (yet).</h2>
          <p className="blog-home__empty-copy">
            {trimmedQuery ? (
              <>
                No posts match{" "}
                <span className="blog-home__empty-query">
                  &ldquo;{trimmedQuery}&rdquo;
                </span>
                . Try something a little broader.
              </>
            ) : (
              "No posts in this category yet."
            )}
          </p>
        </div>
      ) : null}

      {showLoadMore ? (
        <div className="blog-home__load-more-wrap">
          <button
            type="button"
            className="blog-home__load-more"
            onClick={() => setVisibleCount(filteredPosts.length)}
          >
            LOAD MORE
          </button>
        </div>
      ) : null}
    </section>
  );
}
